<script>
import { renderAsync } from 'docx-preview';
import {
  captureTextSelection,
  applyTextHighlights,
  clearTextHighlights,
  scrollToHighlight,
} from '../../utils/textSelection';

export default {
  name: 'DocxViewer',
  props: {
    file: {
      type: Object,
      required: true,
    },
    annotations: {
      type: Array,
      default: () => [],
    },
    usersColor: {
      type: Object,
      default: () => ({}),
    },
    activeAnnotationId: {
      type: [Number, String],
      default: 0,
    },
  },
  data() {
    return {
      loading: true,
      error: null,
      selectionPopover: null,
      pendingSelection: null,
    };
  },
  watch: {
    annotations: {
      handler() {
        this.$nextTick(() => this.renderHighlights());
      },
      deep: true,
    },
    usersColor: {
      handler() {
        this.$nextTick(() => this.renderHighlights());
      },
      deep: true,
    },
    activeAnnotationId(newId) {
      if (newId && this.$refs.docxContainer) {
        scrollToHighlight(this.$refs.docxContainer, newId);
      }
    },
  },
  methods: {
    async loadDocument() {
      this.loading = true;
      this.error = null;

      try {
        // originalFileKey already has CDN prefix applied by updateCdnLinks in proofView.v2
        const cdnUrl = this.file.originalFileKey;

        const response = await fetch(cdnUrl);
        if (!response.ok) throw new Error('Failed to fetch document');

        const arrayBuffer = await response.arrayBuffer();
        const container = this.$refs.docxContainer;

        // Clear previous render
        container.innerHTML = '';

        await renderAsync(arrayBuffer, container, null, {
          className: 'docx-preview',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: true,
          ignoreFonts: false,
          breakPages: false,
          ignoreLastRenderedPageBreak: true,
          experimental: true,
          trimXmlDeclaration: true,
          useBase64URL: true,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true,
        });

        this.loading = false;
        this.processLinks();
        this.$emit('loaded', this.file);

        // Apply highlights after render
        this.$nextTick(() => this.renderHighlights());
      } catch (err) {
        this.error = err.message || 'Failed to load document';
        this.loading = false;
        console.error('DocxViewer: Failed to load document', err);
      }
    },

    handleMouseUp() {
      // Delay to let browser finalize selection
      this.processSelection(10);
    },

    handleTouchEnd() {
      // Longer delay for mobile — browser needs more time to finalize touch selection
      this.processSelection(300);
    },

    processSelection(delay) {
      setTimeout(() => {
        const container = this.$refs.docxContainer;
        if (!container) return;

        const selectionData = captureTextSelection(container);
        this.hideSelectionPopover();

        if (selectionData) {
          this.pendingSelection = selectionData;
          this.showSelectionPopover();
        }
      }, delay);
    },

    showSelectionPopover() {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      const containerRect = this.$refs.docxContainer.getBoundingClientRect();

      let left = rect.left - containerRect.left + (rect.width / 2) - 45;
      // Clamp so popover stays within container bounds
      const popoverWidth = 100;
      left = Math.max(4, Math.min(left, containerRect.width - popoverWidth - 4));

      this.selectionPopover = {
        top: rect.top - containerRect.top - 40,
        left,
      };
    },

    hideSelectionPopover() {
      this.selectionPopover = null;
    },

    handleAddComment() {
      if (!this.pendingSelection) return;
      this.$emit('text-selected', this.pendingSelection);
      this.hideSelectionPopover();
      this.pendingSelection = null;
      window.getSelection().removeAllRanges();
    },

    renderHighlights() {
      const container = this.$refs.docxContainer;
      if (!container) return;

      const textAnnotations = (this.annotations || []).filter(
        a => a.type === 'text' && a.textSelectionStart != null
      );

      applyTextHighlights(container, textAnnotations, this.usersColor, (annotationId) => {
        this.$emit('highlight-clicked', annotationId);
      });

      this.processLinks();
    },

    processLinks() {
      const container = this.$refs.docxContainer;
      if (!container) return;

      const links = container.querySelectorAll('a[href]');
      links.forEach(link => {
        const href = link.getAttribute('href');
        if (!href || href.startsWith('#')) return;

        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');

        if (link.dataset.linkProcessed) return;
        link.dataset.linkProcessed = 'true';

        link.addEventListener('click', (e) => {
          const selection = window.getSelection();
          if (selection && !selection.isCollapsed) {
            e.preventDefault();
            return;
          }
          e.stopPropagation();
        });
      });
    },

    scrollToAnnotation(annotationId) {
      if (this.$refs.docxContainer) {
        scrollToHighlight(this.$refs.docxContainer, annotationId);
      }
    },
  },
  mounted() {
    this.loadDocument();
  },
  beforeDestroy() {
    clearTextHighlights(this.$refs.docxContainer);
  },
};
</script>

<template>
  <div class="docx-viewer-wrapper" ref="docxWrapper">
    <div v-if="loading" class="d-flex align-items-center justify-content-center py-5">
      <div class="spinner-border text-secondary" role="status">
        <span class="sr-only">Loading document...</span>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger m-3">
      {{ error }}
    </div>

    <div
      ref="docxContainer"
      class="docx-container"
      @mouseup="handleMouseUp"
      @touchend="handleTouchEnd"
    ></div>

    <!-- Selection popover for adding comments -->
    <div
      v-if="selectionPopover"
      class="selection-popover"
      :style="{ top: selectionPopover.top + 'px', left: selectionPopover.left + 'px' }"
    >
      <button
        class="btn btn-sm btn-success"
        @click.stop="handleAddComment"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-1"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
        Comment
      </button>
    </div>
  </div>
</template>

<style scoped>
.docx-viewer-wrapper {
  position: relative;
  width: 100%;
  overflow: auto;
}

.docx-container {
  min-height: 200px;
  background: white;
  padding: 0;
}

.selection-popover {
  position: absolute;
  z-index: 1000;
  animation: fadeIn 0.15s ease-in;
}

.selection-popover .btn {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  white-space: nowrap;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 6px;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>

<style>
/* Global styles for docx-preview rendered content */
.docx-preview {
  background: white !important;
  width: 100% !important;
}

.docx-preview-wrapper {
  background: transparent !important;
  padding: 0 !important;
}

.docx-preview-wrapper {
  background: white;
  max-width: 100%;
}

.docx-preview-wrapper > section.docx-preview {
  box-shadow: none;
  margin: 0 auto;
  padding: 40px 60px;
}

.docx-preview a[href] {
  color: #1a73e8;
  text-decoration: underline;
  cursor: pointer;
}

.docx-preview a[href]:hover {
  color: #1558b0;
  text-decoration: underline;
}

@media (max-width: 767px) {
  .docx-preview-wrapper > section.docx-preview {
    padding: 16px 12px !important;
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    box-sizing: border-box;
  }
}
</style>
